import React, { useCallback, useEffect, useRef, useState, Fragment } from 'react';
import BrandLoader from '../shared/brand/BrandLoader';
import { getIntlLocale } from '../../utils/functions';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import { formatRelativeTime } from '../shared/feed/feedTime';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import CollaboratorStack from '../shared/access/CollaboratorStack';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import clsx from 'clsx';
import { Menu, Transition } from '@headlessui/react';
import {
  PiPlayLight,
  PiSpinnerGapLight,
  PiDotsThreeVerticalLight,
  PiClockCounterClockwiseLight,
  PiFileTextLight,
  PiScalesLight,
} from 'react-icons/pi';
import {
  fetchReviewDetail,
} from '../../store/actions/Review/ReviewActions';
import { ReviewSessionSummary } from '../../types/review';
import { useReviewSessionPubSub } from './useReviewSessionPubSub';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, playbookSessionsTourSteps } from '../shared/FeatureTour';

import ActivityDrawer from '../shared/activity/ActivityDrawer';

interface ReviewSessionsProps {
  reviewUuid: string;
  sessions: ReviewSessionSummary[];
}

const formatDate = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(getIntlLocale(), { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};


const statusLabels: Record<string, string> = {
  processing: 'Processing',
  pending: 'Pending',
  completed: 'Completed',
  completed_with_errors: 'Completed with errors',
  failed: 'Failed',
};

const ReviewSessions: React.FC<ReviewSessionsProps> = ({ reviewUuid, sessions }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const [auditTarget, setAuditTarget] = useState<{ type: string; id: string; name: string } | null>(null);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'playbook-sessions',
    steps: playbookSessionsTourSteps,
    readyToShow: true,
    autoStartDelay: 600,
  });

  // Track the most recent processing session for PubSub
  const activeSession = sessions.find(
    (s) => s.status === 'processing' || s.status === 'pending',
  );

  // Connect to PubSub for the active session
  useReviewSessionPubSub(activeSession?.uuid, reviewUuid, !!activeSession);

  // Re-fetch review detail when a processing session completes (via PubSub)
  const prevActiveRef = useRef(activeSession?.uuid);
  useEffect(() => {
    if (prevActiveRef.current && !activeSession) {
      // Active session went away — it completed or failed, refresh
      dispatch(fetchReviewDetail(reviewUuid) as any);
    }
    prevActiveRef.current = activeSession?.uuid;
  }, [activeSession, reviewUuid, dispatch]);

  return (
    <div className="p-6 space-y-6">
      {/* Start new session */}
      <div data-tour-id="playbook-sessions-start">
        <div className="flex items-center justify-between p-4 bg-surface-sunken/70 rounded-lg border border-hairline">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center shrink-0">
              <PiPlayLight className="w-4 h-4 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('Start a review with this playbook')}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('Upload contracts and run them against your rules in the Reviews section.')}
              </p>
            </div>
          </div>
          <Button
            variant="primary"
            size="small"
            onClick={() => history.push(`/reviews?add=true&playbook=${reviewUuid}`)}
          >
            {t('Go to Reviews')}
          </Button>
        </div>
      </div>

      {/* Past sessions */}
      {sessions.length > 0 && (
        <div data-tour-id="playbook-sessions-list">
          <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-3">
            {t('Past Sessions')}
          </h3>
          <div className="space-y-2">
            {sessions.map((session) => {
              const isActive = session.status === 'processing' || session.status === 'pending';

              const riskScore = session.overall_risk_score ?? null;
              const riskBorder = riskScore === null ? 'border-l-gray-200'
                : riskScore >= 7 ? 'border-l-red-500'
                : riskScore >= 4 ? 'border-l-orange-400'
                : 'border-l-emerald-500';
              const statusLabel = statusLabels[session.status] || session.status;
              const statusBadgeColor = isActive ? 'blue'
                : session.status === 'completed' ? 'green'
                : session.status === 'completed_with_errors' ? 'orange'
                : session.status === 'failed' ? 'red'
                : 'gray';

              return (
                <div
                  key={session.uuid}
                  className={clsx(
                    'bg-surface border border-hairline border-l-4 cursor-pointer hover:shadow-sm transition-all',
                    isActive ? 'border-l-blue-500' : riskBorder,
                  )}
                  onClick={() => history.push(`/reviews/${session.uuid}`)}
                >
                  {/* Top row: status + meta + actions */}
                  <div className="flex items-center justify-between gap-3 p-3">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {isActive && <BrandLoader size="xs" className="!h-3.5 !w-3.5 shrink-0" />}
                      <Badge color={statusBadgeColor}>{t(statusLabel)}</Badge>
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <PiFileTextLight className="w-3.5 h-3.5" />
                        {session.document_count === 1 ? t('1 document') : t('{{count}} documents', { count: session.document_count })}
                      </span>
                      {(session as any).has_access && (session as any).has_access.length > 0 && (
                        <CollaboratorStack
                          collaborators={(session as any).has_access}
                          max={4}
                          className="ml-1"
                        />
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-auto">
                      <span className="text-xs text-gray-400" title={formatDate(session.created_at)}>
                        {formatRelativeTime(session.created_at || undefined, t)}
                      </span>
                      <Menu as="div" className="relative">
                        <Menu.Button
                          className="p-1 hover:bg-surface-sunken transition-colors"
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        >
                          <PiDotsThreeVerticalLight className="w-4 h-4 text-gray-400" />
                        </Menu.Button>
                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items className="absolute right-0 mt-1 w-40 bg-surface border border-hairline shadow-lg z-20 py-1">
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  className={clsx(
                                    'flex items-center gap-2 w-full px-3 py-2 text-sm text-left',
                                    active ? 'bg-surface-sunken' : '',
                                  )}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAuditTarget({ type: 'review_session', id: session.uuid, name: `Session ${formatDate(session.created_at)}` });
                                  }}
                                >
                                  <PiClockCounterClockwiseLight className="w-4 h-4 text-gray-500" />
                                  {t('Activity')}
                                </button>
                              )}
                            </Menu.Item>
                          </Menu.Items>
                        </Transition>
                      </Menu>
                    </div>
                  </div>

                  {/* Findings + risk score — only if we have data */}
                  {(session.findings_count > 0 || (riskScore !== null && !isActive)) && (
                    <div className="px-3 pb-3 pt-0 flex items-center gap-3 flex-wrap">
                      {session.findings_count > 0 && (
                        <>
                          {session.critical_count > 0 && <Badge color="red">{session.critical_count} {t('critical')}</Badge>}
                          {session.major_count > 0 && <Badge color="orange">{session.major_count} {t('major')}</Badge>}
                          {session.minor_count > 0 && <Badge color="yellow">{session.minor_count} {t('minor')}</Badge>}
                          {session.info_count > 0 && <Badge color="blue">{session.info_count} {t('info')}</Badge>}
                          <span className="text-[11px] text-gray-400">
                            {session.findings_count} {t('total findings')}
                          </span>
                        </>
                      )}
                      {riskScore !== null && !isActive && (
                        <span className="text-xs text-gray-500 flex items-center gap-1 ml-auto">
                          <PiScalesLight className="w-3.5 h-3.5" />
                          {t('Risk score')}:
                          <span className={clsx(
                            'font-semibold',
                            riskScore >= 7 ? 'text-danger-600' :
                            riskScore >= 4 ? 'text-warning-500' : 'text-success-600',
                          )}>{Number(riskScore).toFixed(1)}</span>
                          <span className="text-gray-400">/ 10</span>
                        </span>
                      )}
                    </div>
                  )}

                  {isActive && session.findings_count > 0 && (
                    <div className="px-3 pb-3 text-xs text-info-500 font-medium">
                      {session.findings_count} {t('findings so far...')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {sessions.length === 0 && (
        <div className="text-center py-8 border-2 border-dashed border-hairline rounded-lg">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('No review sessions yet. Click "Start New Review" to run your first review.')}
          </p>
        </div>
      )}
      {auditTarget && (
        <ActivityDrawer onClose={() => setAuditTarget(null)}>
          <AuditLogPanel
            entityType="review"
            entityId={auditTarget.id}
            entityName={auditTarget.name}
            onClose={() => setAuditTarget(null)}
          />
        </ActivityDrawer>
      )}

      <FeatureTour
        steps={playbookSessionsTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </div>
  );
};

export default ReviewSessions;
